// /frontend/src/api/PublicationPageApi.js

import axios from 'axios';
import { handleApiError } from './handleApiError';

const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

export const fetchProductsForPublication = async (token, influencerId) => {
  const url = `${baseUrl}/publications/api/getProducts/${influencerId}`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    // ALWAYS return the structured success object
    return { status: true, data: response.data };
  } catch (error) {
    // Let the centralized handler create the structured error object
    return handleApiError(error, 'fetchProductsForPublication');
  }
};

/**
 * Fetches all publications for a given influencer.
 * @returns {Promise<{status: boolean, data?: object[], message?: string}>}
 */
export const fetchPublicationsByInfluencer = async (token, influencerId) => {
  const url = `${baseUrl}/publications/api/publications/${influencerId}`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'fetchPublicationsByInfluencer');
  }
};

/**
 * Fetches the list of all available products to display in the "Add Publication" form.
 * Renamed from fetchProductsToPublish for clarity.
 * @returns {Promise<{status: boolean, data?: object[], message?: string}>}
 */
export const fetchProductsForPublicationPage = async (token, influencerId) => {
  const url = `${baseUrl}/publications/api/getProducts/${influencerId}`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'fetchProductsForPublicationPage');
  }
};

// --- Publication CRUD Actions ---

/**
 * Creates a new publication record.
 * @returns {Promise<{status: boolean, data?: object, message?: string}>}
 */
export const createPublication = async (token, payload) => {
  const url = `${baseUrl}/publications/api/createPublication`;
  try {
    const response = await axios.post(url, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'createPublication');
  }
};

/**
 * Deletes a publication record.
 * @returns {Promise<{status: boolean, data?: object, message?: string}>}
 */
export const deletePublication = async (token, publicationId) => {
  const url = `${baseUrl}/publications/api/deletePublication/${publicationId}`;
  try {
    const response = await axios.delete(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'deletePublication');
  }
};

/**
 * Adds a product to a specific publication's list of published products.
 * @returns {Promise<{status: boolean, data?: object, message?: string}>}
 */
export const addPublishedProduct = async (token, publicationId, productId) => {
  const url = `${baseUrl}/publications/api/publications/${publicationId}/products`;
  try {
    const response = await axios.post(
      url,
      { productId },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    // On success, the backend should return the updated list of products for that publication
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'addPublishedProduct');
  }
};

/**
 * Deletes a product from a specific publication's list of published products.
 * @returns {Promise<{status: boolean, data?: object, message?: string}>}
 */
export const deletePublishedProduct = async (
  token,
  publicationId,
  productId,
) => {
  const url = `${baseUrl}/publications/api/publications/${publicationId}/products/${productId}`;
  try {
    const response = await axios.delete(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    // On success, the backend should also return the updated list of products
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'deletePublishedProduct');
  }
};

/**
 * Toggles a product's 'isSelected' status for a specific publication.
 * @returns {Promise<{status: boolean, data?: object, message?: string}>}
 */
export const togglePublishedProduct = async (
  token,
  publicationId,
  productId,
) => {
  const url = `${baseUrl}/publications/api/publications/${publicationId}/products/toggle`;
  try {
    const response = await axios.post(
      url,
      { productId },
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'togglePublishedProduct');
  }
};

/**
 * Toggles the 'includeVideoData' flag for a publication.
 * @returns {Promise<{status: boolean, data?: object, message?: string}>}
 */
export const toggleIncludeVideoData = async (token, publicationId) => {
  const url = `${baseUrl}/publications/api/publications/${publicationId}/video/toggle`;
  try {
    const response = await axios.post(
      url,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'toggleIncludeVideoData');
  }
};

/**
 * Refreshes the metadata for a specific publication from its source (e.g., YouTube).
 * @param {string} token - The user's JWT.
 * @param {number} publicationId - The ID of the publication to refresh.
 * @returns {Promise<{status: boolean, data?: object, message?: string}>}
 */
export const refreshPublicationMetadata = async (
  token,
  influencerId,
  publicationId,
) => {
  const url = `${baseUrl}/publications/api/publications/${publicationId}/refresh`;
  try {
    // A POST request is appropriate as this action modifies server state.
    const response = await axios.post(
      url,
      { influencerId },
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    // On success, the backend returns the full, updated publication object.
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'refreshPublicationMetadata');
  }
};

// --- AI Summary & Analysis Actions ---

/**
 * Generates a new AI summary for a public YouTube video.
 * @returns {Promise<{status: boolean, data?: object, message?: string}>}
 */
export const generatePublicationAISummary = async (token, params) => {
  const url = `${baseUrl}/publications/api/generate-summary`;
  try {
    const response = await axios.post(url, params, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 180000,
    });
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'generatePublicationAISummary');
  }
};

/**
 * Generates a new AI summary for a private/unlisted YouTube video.
 * @returns {Promise<{status: boolean, data?: object, message?: string}>}
 */
export const generatePrivatePublicationAISummary = async (token, params) => {
  const url = `${baseUrl}/publications/api/generate-private-summary`;
  try {
    const response = await axios.post(url, params, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 600000,
    });
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'generatePrivatePublicationAISummary');
  }
};

/**
 * Fetches an existing AI summary for a publication.
 * @returns {Promise<{status: boolean, data?: object, message?: string}>}
 */
export const fetchPublicationAiSummary = async (token, publicationId) => {
  const url = `${baseUrl}/publications/api/get-summary/${publicationId}`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'fetchPublicationAiSummary');
  }
};

/**
 * Updates (saves) a manually edited AI summary.
 * @returns {Promise<{status: boolean, data?: object, message?: string}>}
 */
export const updatePublicationAISummary = async (
  token,
  publicationId,
  newSummary,
) => {
  const url = `${baseUrl}/publications/api/update-summary/${publicationId}`;
  try {
    const response = await axios.patch(
      url,
      { aiSummary: newSummary },
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'updatePublicationAISummary');
  }
};

// --- Background Job Management ---

/**
 * Starts the background content analysis job for all publications of an influencer.
 * @param {string} token - The user's JWT.
 * @param {string} influencerId - The ID of the influencer whose publications will be analyzed.
 * @returns {Promise<object>} A promise resolving to { jobId } or a structured error.
 */
export const startContentAnalysisJob = async (
  token,
  influencerId,
  publicationsToAnalyze,
  forceReanalysis,
) => {
  const url = `${baseUrl}/publications/api/start-content-analysis`;
  try {
    const response = await axios.post(
      url,
      { influencerId, publicationsToAnalyze, forceReanalysis },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        validateStatus: (status) => status >= 200 && status < 500,
      },
    );

    if (response.status === 202) {
      return { status: true, data: response.data }; // { jobId: "..." }
    } else {
      return {
        status: false,
        message: response.data.message || 'Failed to start the analysis job.',
        error: response.data.error || `HTTP_ERROR_${response.status}`,
      };
    }
  } catch (error) {
    return handleApiError(error, 'startContentAnalysisJob');
  }
};

/**
 * Checks the status of a specific content analysis background job.
 * @param {string} token - The user's JWT.
 * @param {string} jobId - The ID of the job to check.
 * @returns {Promise<object>} A promise resolving to the job status object or a structured error.
 */
export const checkContentAnalysisJobStatus = async (token, jobId) => {
  const url = `${baseUrl}/publications/api/content-analysis-status/${jobId}`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      return { status: true, data: response.data }; // { status, data?, message? }
    } else {
      return {
        status: false,
        message: response.data.message || 'Failed to retrieve job status.',
        error: response.data.error || `HTTP_ERROR_${response.status}`,
      };
    }
  } catch (error) {
    return handleApiError(error, 'checkContentAnalysisJobStatus');
  }
};

export const cancelContentAnalysisJob = async (token, jobId) => {
  // FIXED: Added '/api' to the path to match your Blueprint config
  const url = `${baseUrl}/publications/api/jobs/${jobId}/cancel`;

  try {
    const response = await axios.post(
      url,
      {}, // Empty body for POST request
      {
        headers: { Authorization: `Bearer ${token}` },
        validateStatus: (status) => status >= 200 && status < 300,
      },
    );

    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'cancelContentAnalysisJob');
  }
};

// --- Individual Market Fit Analysis ---
/**
 * Generates a market fit analysis for a single publication video.
 * @param {string} token - The user's JWT.
 * @param {object} params - { publicationId, videoUrl }
 * @returns {Promise<object>} A promise resolving to the market fit data or a structured error.
 */
export const generateSingleMarketFitAnalysis = async (token, params) => {
  console.warn(
    "API function 'generateSingleMarketFitAnalysis' is a placeholder.",
  );
  // This would point to a new backend endpoint, e.g., `/publications/api/generate-market-fit`
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        status: false,
        message: 'This feature is not yet implemented.',
      });
    }, 1000);
  });
};

/**
 * Fetches the persisted individual market fit analysis for a single publication.
 * This function calls the dedicated endpoint that retrieves the analysis from the
 * publication_market_fit table.
 *
 * @param {string} token - The user's JWT for authentication.
 * @param {number} publicationId - The unique ID of the publication to fetch the analysis for.
 * @returns {Promise<object>} A promise that resolves to a standardized response object.
 *                            On success: { status: true, data: { ...analysisObject } }
 *                            On failure: { status: false, message: "...", error: "..." }
 */
export const fetchIndividualMarketFitAnalysis = async (
  token,
  publicationId,
) => {
  // Construct the correct URL for the API endpoint
  const url = `${baseUrl}/publications/api/market-fit/${publicationId}`;

  try {
    // Make the authenticated GET request
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // On success, return the standardized success object with the data payload
    return { status: true, data: response.data };
  } catch (error) {
    // On failure, delegate to the centralized error handler to ensure a
    // consistent error response structure throughout the application.
    return handleApiError(error, 'fetchIndividualMarketFitAnalysis');
  }
};

// --- Market Fit & Aggregated Analysis ---

/**
 * Fetches the most recent aggregated market fit analysis for an influencer.
 * @param {string} token - The user's JWT.
 * @param {number} influencerId - The ID of the influencer.
 * @returns {Promise<object>} A promise resolving to the analysis data or a structured error.
 */
export const fetchAggregatedMarketFit = async (token, influencerId) => {
  const url = `${baseUrl}/publications/api/aggregated-market-fit/${influencerId}`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    // The endpoint returns the data object directly, or null
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'fetchAggregatedMarketFit');
  }
};

/**
 * Fetches the most recent aggregated publication quality summary for an influencer.
 * @param {string} token - The user's JWT.
 * @param {number} influencerId - The ID of the influencer.
 * @returns {Promise<object>} A promise resolving to the summary data or a structured error.
 */
export const fetchAggregatedPublicationSummary = async (
  token,
  influencerId,
) => {
  const url = `${baseUrl}/publications/api/aggregated-publication-summary/${influencerId}`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'fetchAggregatedPublicationSummary');
  }
};
